import React, { useEffect, useRef, useState } from "react";

import { LangProvider } from "./context/LangContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import DemoVideo from "./pages/DemoVideo";
import About from "./pages/About";
import OurSolutions from "./pages/OurSolutions";
import Collaborators from "./pages/Collaborators";
import Contact from "./pages/Contact";
import UseCases from "./pages/UseCases";
import OurMilestones from "./pages/OurMilestones";
import OurTeam from "./pages/OurTeam";

import AppShell from "./components/layout/AppShell";
import "./styles/App.css";

import Popup from "./components/Popup";
import popupImage from "./assets/19_Mayıs.png";

import CookieBanner from "./components/consent/CookieBanner";
import { initAnalytics, trackPageView } from "./utils/analytics";

const ENABLE_POPUP = false;

export default function App() {
  const isAnimatingRef = useRef(false);
  const wheelLockRef = useRef(false);
  const lastWheelTimeRef = useRef(0);

  const [showPopup, setShowPopup] = useState(ENABLE_POPUP);

  useEffect(() => {
    initAnalytics();

    const handleRouteChange = () => {
      trackPageView(
        window.location.pathname + window.location.search + window.location.hash
      );
    };

    window.addEventListener("hashchange", handleRouteChange);
    window.addEventListener("popstate", handleRouteChange);

    return () => {
      window.removeEventListener("hashchange", handleRouteChange);
      window.removeEventListener("popstate", handleRouteChange);
    };
  }, []);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const cleanUrl = window.location.pathname;

    if (window.location.search || window.location.hash) {
      window.history.replaceState(null, "", cleanUrl);
    }

    const raf = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });

    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const isTouchDevice = () =>
      window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
      window.innerWidth < 992;

    const isDesktopSnapAllowed = () =>
      !isTouchDevice() &&
      window.matchMedia("(prefers-reduced-motion: no-preference)").matches;

    const getSections = () =>
      Array.from(document.querySelectorAll(".page-section")).filter(
        (el) => el instanceof HTMLElement
      );

    const getFooter = () => document.querySelector("footer");

    const getNavbarOffset = () => {
      const rootStyles = getComputedStyle(document.documentElement);

      const navH = parseFloat(rootStyles.getPropertyValue("--nav-h")) || 66;

      const navTopGap =
        parseFloat(rootStyles.getPropertyValue("--nav-offset-top")) || 10;

      return navH + navTopGap + 14;
    };

    const getScrollTargetTop = (element) => {
      if (!(element instanceof HTMLElement)) return 0;

      return Math.max(0, element.offsetTop - getNavbarOffset());
    };

    const getClosestStepIndex = (steps) => {
      const currentY = window.scrollY;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      steps.forEach((step, index) => {
        const targetTop = getScrollTargetTop(step);
        const distance = Math.abs(targetTop - currentY);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const scrollToStep = (step) => {
      if (!step) return;

      isAnimatingRef.current = true;

      window.scrollTo({
        top: getScrollTargetTop(step),
        left: 0,
        behavior: "smooth",
      });

      window.setTimeout(() => {
        isAnimatingRef.current = false;
      }, 640);
    };

    const onWheel = (e) => {
      if (!isDesktopSnapAllowed()) return;

      const mobileMenuOpen = document.querySelector(".mobile-menu.is-open");
      if (mobileMenuOpen) return;

      const now = Date.now();

      if (now - lastWheelTimeRef.current < 140) {
        e.preventDefault();
        return;
      }

      lastWheelTimeRef.current = now;

      if (wheelLockRef.current || isAnimatingRef.current) {
        e.preventDefault();
        return;
      }

      if (Math.abs(e.deltaY) < 24) return;

      const sections = getSections();
      const footer = getFooter();
      const steps = footer ? [...sections, footer] : [...sections];

      if (!steps.length) return;

      const currentIndex = getClosestStepIndex(steps);
      let nextIndex = currentIndex;

      if (e.deltaY > 0) {
        nextIndex = Math.min(currentIndex + 1, steps.length - 1);
      } else {
        nextIndex = Math.max(currentIndex - 1, 0);
      }

      if (nextIndex === currentIndex) return;

      e.preventDefault();

      wheelLockRef.current = true;
      scrollToStep(steps[nextIndex]);

      window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 700);
    };

    window.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      window.removeEventListener("wheel", onWheel);
    };
  }, []);

  useEffect(() => {
    const isMobileLike =
      window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
      window.innerWidth < 992 ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const animatedItems = Array.from(
      document.querySelectorAll(".page-section, footer")
    ).filter((el) => el instanceof HTMLElement);

    if (!animatedItems.length) return;

    if (isMobileLike) {
      animatedItems.forEach((el) => {
        el.classList.remove("reveal-ready");
        el.classList.add("reveal-in");
      });

      return;
    }

    animatedItems.forEach((el) => {
      el.classList.add("reveal-ready");
    });

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-in");
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -6% 0px",
      }
    );

    animatedItems.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <LangProvider>
      <AppShell>
        <div className="app-root">
          <Navbar />

          {ENABLE_POPUP && (
            <Popup
              imageSrc={popupImage}
              show={showPopup}
              autoCloseMs={10000}
              onClose={() => setShowPopup(false)}
            />
          )}

          <main className="main-content">
            <DemoVideo />
            <OurSolutions />
            <UseCases />
            <About />
            <OurMilestones />
            <Collaborators />
            <OurTeam />
            <Contact />
          </main>

          <Footer />
          <CookieBanner />
        </div>
      </AppShell>
    </LangProvider>
  );
}